import React from 'react';
import SEO from '../SEO';
import { 
  ArrowRight, Download, Shield, 
  ChevronRight, Sparkles, Layers, Fingerprint, Zap
} from 'lucide-react';
import { motion } from 'framer-motion';

 const About: React.FC = () => {

  const values = [
    {
      title: "Monolithic Foundations",
      desc: "Robust, scalable systems built to outlast trends.",
      icon: Shield,
      color: "bg-blue-500/10 text-blue-500"
    },
    {
      title: "Strategic Velocity",
      desc: "Rapid deployment without sacrificing precision.",
      icon: Zap,
      color: "bg-amber-500/10 text-amber-500"
    },
    {
      title: "AI First Design",
      desc: "Intelligent UX that predicts and adapts.",
      icon: Sparkles,
      color: "bg-purple-500/10 text-purple-500"
    }
  ];

  return (
    <div className="min-h-screen bg-[#F8F9FA] text-black font-inter selection:bg-black selection:text-white">
      <SEO
        title="About TDMA | Pure Mobile UX"
        description="Experience the digital monolith. TDMA is a human-centric tech agency building for the mobile era."
      />

      {/* App-Style Hero Section (White Background) */}
      <section className="px-5 pt-32 pb-16 bg-white overflow-hidden border-b border-gray-100">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.6 }}
        >
          <div className="inline-flex items-center gap-1.5 px-3 py-1 bg-black text-white rounded-full text-[10px] font-bold tracking-widest uppercase mb-6 shadow-sm">
            <Fingerprint className="w-3 h-3" /> Digital Monolith
          </div>
          <h1 className="text-5xl md:text-8xl font-normal tracking-tight leading-[1] mb-6">
            Building for <br/>
            <span className="text-gray-400">the focus era.</span>
          </h1>
          <p className="text-lg md:text-xl text-gray-500 max-w-lg font-light leading-relaxed">
            TDMA is a digital studio engineering high-performance systems for visionary brands.
          </p>
        </motion.div>
      </section>

      {/* Core Values (Tactile Mobile Cards) */}
      <section className="px-5 py-20 pb-24">
        <div className="flex items-center justify-between mb-8">
          <h2 className="text-xl md:text-3xl font-normal tracking-tight">Philosophy</h2>
          <span className="text-xs text-gray-400 font-medium tracking-widest uppercase">01 / 03</span>
        </div>
        
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {values.map((item, index) => (
            <motion.div
              key={index}
              initial={{ opacity: 0, scale: 0.95 }}
              whileInView={{ opacity: 1, scale: 1 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
              className="p-6 bg-white rounded-3xl border border-gray-100 shadow-sm hover:shadow-md transition-shadow group cursor-pointer"
            >
              <div className={`w-12 h-12 rounded-2xl flex items-center justify-center mb-6 ${item.color}`}>
                <item.icon className="w-6 h-6" />
              </div>
              <h3 className="text-lg font-medium mb-2">{item.title}</h3>
              <p className="text-sm text-gray-500 font-light leading-relaxed">{item.desc}</p>
            </motion.div>
          ))}
        </div>
      </section>

      {/* Dark Narrative Section (Mobile Transition) */}
      <section className="bg-black text-white px-5 py-24 rounded-t-[3rem] -mt-8">
        <div className="max-w-4xl mx-auto space-y-20">
          <motion.div
            initial={{ opacity: 0 }}
            whileInView={{ opacity: 1 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-6xl font-normal tracking-tight leading-tight mb-8">
              A solid foundation in <br /> 
              <span className="text-[#FF4E45]">lightspeed evolution.</span>
            </h2>
            <p className="text-lg text-white/50 max-w-2xl font-light leading-relaxed">
              We reject the fragile. In an era of fleeting trends, TDMA builds systems that endure. Our methodology centers on speed, scalability, and relentless impact.
            </p>
          </motion.div>

          {/* Minimalist Team List */}
        </div>
      </section>

      {/* The Deck (Premium Overlay App Feel) */}
      <section className="px-5 py-24 mb-10">
        <div className="bg-white rounded-[2.5rem] p-6 pt-10 md:p-12 shadow-2xl border border-gray-100 overflow-hidden relative">
          <div className="absolute top-0 right-0 p-6">
            <div className="w-10 h-10 rounded-full bg-gray-50 flex items-center justify-center text-gray-400">
              <Layers className="w-5 h-5" />
            </div>
          </div>
          
          <h2 className="text-3xl font-normal tracking-tight mb-4">Digital Deck</h2>
          <p className="text-sm text-gray-400 mb-10 max-w-xs leading-relaxed">
            Explore our capabilities, process, and vision in our official showcase deck.
          </p>

          <div className="rounded-2xl overflow-hidden bg-gray-50 border border-gray-100 h-[50vh] md:h-[70vh] mb-8">
            <iframe
              src="/pdf/tdma.pdf"
              title="TDMA Showcase"
              className="w-full h-full border-none grayscale-[40%] hover:grayscale-0 transition-all duration-500"
            />
          </div>

          <a 
            href="/pdf/tdma.pdf" 
            download 
            className="w-full sm:w-auto inline-flex items-center justify-center gap-2 bg-black text-white px-8 py-5 rounded-full font-bold text-sm hover:scale-[1.02] active:scale-95 transition-all shadow-xl"
          >
            <Download className="w-4 h-4" /> Download Official PDF
          </a>
        </div>
      </section>

      {/* Final Mobile CTA */}
      <section className="px-5 pb-32 text-center">
        <motion.div 
          whileHover={{ y: -5 }}
          className="bg-black text-white p-12 rounded-[3.5rem] relative overflow-hidden group"
        >
          <div className="absolute inset-0 bg-gradient-to-br from-[#FF4E45]/40 to-transparent opacity-0 group-hover:opacity-100 transition-opacity duration-700" />
          <h2 className="text-3xl font-normal mb-8 relative z-10">Let's build <br/> together.</h2>
          <a
            href="/contact"
            className="inline-flex items-center gap-2 bg-white text-black px-10 py-5 rounded-full font-bold text-sm relative z-10 hover:shadow-2xl transition-all"
          >
            Contact Studio <ArrowRight className="w-4 h-4" />
          </a>
        </motion.div>
      </section>
    </div>
  );
};

export default About;